import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

type ChipVariant =
  | 'blue'
  | 'green'
  | 'primaryBlue'
  | 'yellow'
  | 'red'
  | 'disabled'
  | 'dashed'
  | 'transparent'
  | 'default'

type ChipSize = 'small' | 'medium' | 'large' | 'tag'

interface ChipProps {
  variant?: ChipVariant
  size?: ChipSize
  text?: string
  icon?: React.ReactNode
  borderRadius?: string
  paddingLeft?: string
  paddingRight?: string
  className?: string
  textStyle?: string
}

export const Chip = ({
  variant = 'default',
  size = 'small',
  text,
  icon,
  borderRadius,
  paddingLeft,
  paddingRight,
  className = '',
  textStyle,
}: ChipProps) => {
  const colorMap: Record<
    ChipVariant,
    {
      bgColor: string
      textColor: string
      border: string
    }
  > = {
    blue: {
      bgColor: '#C6F5FF',
      textColor: '#037797',
      border: 'transparent',
    },
    green: {
      bgColor: '#C8FFD4',
      textColor: '#1E924D',
      border: 'transparent',
    },
    primaryBlue: {
      bgColor: '#087FFF',
      textColor: '#F5F5F5',
      border: '#087FFF',
    },
    disabled: {
      bgColor: 'neutral-200',
      textColor: 'neutral-400',
      border: 'transparent',
    },
    default: {
      bgColor: '',
      textColor: '',
      border: '',
    },
    yellow: {
      bgColor: '#FFFDCB',
      textColor: '#DFB014',
      border: 'transparent',
    },
    red: {
      bgColor: SocialMirrorPalette.alertRedBase,
      textColor: SocialMirrorPalette.dynamicRed,
      border: SocialMirrorPalette.alertRedBase,
    },
    dashed: {
      bgColor: 'transparent',
      textColor: SocialMirrorPalette.neutralColor60,
      border: SocialMirrorPalette.neutralColor60,
    },
    transparent: {
      bgColor: SocialMirrorPalette.baseColorHighlight,
      textColor: SocialMirrorPalette.neutralColor,
      border: SocialMirrorPalette.neutralColor60,
    },
  }

  const sizeStyles: Record<ChipSize, string> = {
    small: 'h-5 px-1 py-0.5 rounded',
    medium: 'h-[35px] px-4 py-2 rounded-[200px]',
    large: 'h-10 px-3 py-1 rounded-[200px]',
    tag: 'h-6 px-3 py-1 rounded-[200px]',
  }

  const { bgColor, textColor, border } = colorMap[variant]
  const isDashed = variant === 'dashed'

  return (
    <div
      className={`flex items-center justify-center gap-1 ${sizeStyles[size]} ${className}`}
      style={{
        backgroundColor: bgColor,
        borderRadius: borderRadius,
        paddingRight: paddingRight,
        paddingLeft: paddingLeft,
        border: `0.5px ${isDashed ? 'dashed' : 'solid'} ${border}`,
      }}
    >
      <p
        className={`text-xs font-medium tracking-tight text-nowrap ${textStyle}`}
        style={{ color: textColor }}
      >
        {text}
      </p>
      {icon && icon}
    </div>
  )
}
